import { useCallback, useEffect, useRef, useState } from "react"
import API from "@/common/API"
import { parsedUser } from "@/common/GetCurrentUser"
import { FeedFilterValues, FeedItem } from "./types"

const PAGE_SIZE = 20
const MAX_ITEMS = 200
const POLL_MS = 60000

export const buildFeedQuery = (filters: FeedFilterValues, extra: Record<string, string | undefined> = {}) => {
  const user: any = parsedUser()
  const params = new URLSearchParams()
  if (user?.OrganizationId) params.set("OrganizationId", user.OrganizationId)
  if (filters.types.length) params.set("Types", filters.types.join(","))
  if (filters.agentId) params.set("AgentId", filters.agentId)
  if (filters.search) params.set("Search", filters.search)
  Object.entries(extra).forEach(([k, v]) => {
    if (v) params.set(k, v)
  })
  return params.toString()
}

export const useFeed = (filters: FeedFilterValues) => {
  const [items, setItems] = useState<FeedItem[]>([])
  const [cursor, setCursor] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(true)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [newCount, setNewCount] = useState(0)
  // Guards against stale responses when filters change mid-request.
  const requestId = useRef(0)
  const loadingRef = useRef(false)

  const fetchPage = useCallback(
    async (before: string | null, reset: boolean) => {
      const id = ++requestId.current
      loadingRef.current = true
      setLoading(true)
      setError(false)
      try {
        const query = buildFeedQuery(filters, { Before: before || undefined, Quantity: String(PAGE_SIZE) })
        const res = await API.getAction("Feed/List", query)
        if (id !== requestId.current) return
        if (res.data?.status !== "ok") throw new Error("feed")
        const page = res.data.response
        setItems(prev => {
          if (reset) return page.items
          const known = new Set(prev.map(i => i.id))
          return [...prev, ...page.items.filter((i: FeedItem) => !known.has(i.id))]
        })
        setCursor(page.nextCursor)
        setHasMore(page.hasMore)
        if (reset) setNewCount(0)
      } catch {
        if (id === requestId.current) setError(true)
      } finally {
        if (id === requestId.current) {
          loadingRef.current = false
          setLoading(false)
        }
      }
    },
    [filters]
  )

  const reload = useCallback(() => {
    setItems([])
    setCursor(null)
    setHasMore(true)
    return fetchPage(filters.before || null, true)
  }, [fetchPage, filters.before])

  useEffect(() => {
    reload()
  }, [reload])

  const loadMore = useCallback(() => {
    if (loadingRef.current || !hasMore || error) return
    fetchPage(cursor, false)
  }, [cursor, hasMore, error, fetchPage])

  // Poll for newer events only when viewing the live head of the feed.
  const newestAt = items[0]?.occurredAt
  useEffect(() => {
    if (!newestAt || filters.before) return
    const check = async () => {
      if (document.visibilityState !== "visible") return
      try {
        const res = await API.getAction("Feed/NewCount", buildFeedQuery(filters, { After: newestAt }))
        if (res.data?.status === "ok") setNewCount(res.data.response || 0)
      } catch {
        // Polling is best-effort; the next tick retries.
      }
    }
    const timer = window.setInterval(check, POLL_MS)
    document.addEventListener("visibilitychange", check)
    return () => {
      window.clearInterval(timer)
      document.removeEventListener("visibilitychange", check)
    }
  }, [newestAt, filters])

  const patchItems = useCallback((match: (i: FeedItem) => boolean, patch: (i: FeedItem) => FeedItem) => {
    setItems(prev => prev.map(i => (match(i) ? patch(i) : i)))
  }, [])

  return {
    items,
    hasMore,
    loading,
    error,
    newCount,
    capped: items.length >= MAX_ITEMS,
    loadMore,
    reload,
    patchItems,
  }
}
